"use client"

import { useMemo, useState } from "react"
import { HttpTypes } from "@medusajs/types"
import { Check, ChevronDown } from "lucide-react"
import COASection from "../product-scientific-authority/coa-section"

type ProductDetailTabsProps = {
    product: HttpTypes.StoreProduct
}

type TabId = "overview" | "specs" | "storage" | "faq" | "coa"

type FaqItem = { q: string; a: string }
type Citation = { title?: string; authors?: string; journal?: string; year?: string; pmid?: string; doi?: string }

/**
 * Parses a single PowerShell-hashtable serialized string, e.g.
 * "@{a=Some answer.; q=Some question?}" → { a: "Some answer.", q: "Some question?" }
 * Splits only at "; key=" boundaries so values containing periods/commas survive.
 */
function parsePsHashtable(str: string): Record<string, string> {
    const inner = str.trim().replace(/^@\{/, "").replace(/\}$/, "")
    const out: Record<string, string> = {}
    const re = /(?:^|; )(\w+)=/g
    const marks = Array.from(inner.matchAll(re))
    marks.forEach((m, i) => {
        const key = m[1]
        const start = (m.index ?? 0) + m[0].length
        const end = i + 1 < marks.length ? (marks[i + 1].index ?? inner.length) : inner.length
        out[key] = inner.slice(start, end).trim()
    })
    return out
}

/**
 * Normalizes a metadata field that may be: a real array of objects, an array of
 * PowerShell-hashtable strings, a JSON string, or undefined.
 */
function normalizeObjectList(value: unknown): Record<string, any>[] {
    if (!value) return []
    let arr: unknown = value
    if (typeof value === "string") {
        try {
            arr = JSON.parse(value)
        } catch {
            return []
        }
    }
    if (!Array.isArray(arr)) return []
    return arr
        .map((item) => {
            if (item && typeof item === "object") return item as Record<string, any>
            if (typeof item === "string") {
                const t = item.trim()
                if (t.startsWith("@{")) return parsePsHashtable(t)
                if (t.startsWith("{")) {
                    try {
                        return JSON.parse(t)
                    } catch {
                        return null
                    }
                }
            }
            return null
        })
        .filter(Boolean) as Record<string, any>[]
}

function normalizeStringList(value: unknown): string[] {
    if (!value) return []
    if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean)
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) return parsed.map((v) => String(v)).filter(Boolean)
        } catch {
            // fall through to newline split
        }
        return value
            .split("\n")
            .map((s) => s.replace(/^[•\-*]\s*/, "").trim())
            .filter(Boolean)
    }
    return []
}

const TEAL = "#005555"

const ProductDetailTabs = ({ product }: ProductDetailTabsProps) => {
    const metadata = (product.metadata || {}) as Record<string, any>

    // --- Overview content ---
    const overview = (metadata.research_synopsis ||
        metadata.research_overview ||
        product.description ||
        "") as string
    const mechanism = (metadata.mechanism_of_action || "") as string
    const areasOfStudy = useMemo(() => normalizeStringList(metadata.areas_of_study), [metadata])
    const applications = useMemo(() => normalizeStringList(metadata.research_applications), [metadata])
    const citations = useMemo(
        () => normalizeObjectList(metadata.citations) as Citation[],
        [metadata]
    )

    // --- Specifications (no fabricated values; purity keeps generic default per policy) ---
    const specs = useMemo(() => {
        return [
            { label: "Molecular Formula", value: metadata.molecular_formula },
            { label: "Molar Mass", value: metadata.molecular_weight || metadata.molar_mass },
            { label: "CAS Number", value: metadata.cas_number || metadata.cas },
            { label: "Amino Acid Sequence", value: metadata.sequence },
            { label: "Synonyms", value: metadata.synonyms },
            { label: "Solubility", value: metadata.solubility },
            { label: "Appearance", value: metadata.appearance || metadata.organoleptic_profile },
            { label: "Composition", value: metadata.composition || metadata.form },
            { label: "Purity (HPLC)", value: metadata.purity || "≥99% (HPLC)" },
        ].filter((s) => s.value) as { label: string; value: string }[]
    }, [metadata])

    // --- Storage & Handling ---
    const storageConditions = (metadata.storage_instructions || metadata.storage || "") as string
    const handlingNotes = (metadata.handling_notes || "") as string
    const stabilityData = (metadata.stability_data || "") as string
    const hasStorage = !!(storageConditions || handlingNotes || stabilityData)

    // --- FAQ ---
    const faqItems = useMemo<FaqItem[]>(() => {
        return normalizeObjectList(metadata.faq)
            .map((item) => ({ q: item.q || item.question || "", a: item.a || item.answer || "" }))
            .filter((item) => item.q && item.a)
    }, [metadata])

    const legacyCoaUrl = (metadata.coa_url || metadata.coa_pdf_url) as string | undefined

    const tabs: { id: TabId; label: string; show: boolean }[] = [
        { id: "faq", label: "FAQ", show: faqItems.length > 0 },
        { id: "overview", label: "Overview", show: !!(overview || mechanism || areasOfStudy.length) },
        { id: "specs", label: "Specifications", show: specs.length > 0 },
        { id: "storage", label: "Storage & Handling", show: hasStorage },
        { id: "coa", label: "Certificate of Analysis", show: true },
    ]
    const visibleTabs = tabs.filter((t) => t.show)
    const [activeTab, setActiveTab] = useState<TabId>(visibleTabs[0]?.id ?? "overview")
    // Mobile accordion: open the first section (FAQ when present) by default.
    const [openMobile, setOpenMobile] = useState<TabId | null>(visibleTabs[0]?.id ?? null)

    if (visibleTabs.length === 0) return null

    const renderContent = (id: TabId) => {
        switch (id) {
            case "overview":
                return (
                    <div className="max-w-3xl space-y-10">
                        {overview && (
                            <Section title="Research Overview">
                                <Paragraphs text={overview} />
                            </Section>
                        )}
                        {mechanism && (
                            <Section title="Mechanism of Action">
                                <Paragraphs text={mechanism} />
                            </Section>
                        )}
                        {areasOfStudy.length > 0 && (
                            <Section title="Areas of Study">
                                <p className="text-slate-600 leading-relaxed mb-4 text-[15px]">
                                    {product.title} is intended for{" "}
                                    <strong className="text-slate-800">laboratory research use only</strong>.
                                    Researchers may investigate the following areas in experimental settings:
                                </p>
                                <ul className="space-y-3">
                                    {areasOfStudy.map((area, i) => (
                                        <li key={i} className="flex gap-3 items-start">
                                            <Check
                                                size={18}
                                                strokeWidth={3}
                                                className="mt-1 flex-shrink-0"
                                                style={{ color: TEAL }}
                                            />
                                            <span className="text-slate-600 leading-relaxed text-[15px]">{area}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}
                        {applications.length > 0 && (
                            <Section title="Research Applications">
                                <ul className="space-y-2">
                                    {applications.map((app, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0" />
                                            <span className="text-slate-600 leading-relaxed text-[15px]">{app}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}
                        {citations.length > 0 && (
                            <Section title="Research References">
                                <ol className="space-y-3 list-decimal list-inside">
                                    {citations.map((c, i) => (
                                        <li key={i} className="text-[13px] text-slate-500 leading-relaxed">
                                            <span className="text-slate-700">{c.authors}</span>
                                            {c.title && <> {c.title}.</>}
                                            {c.journal && <em> {c.journal}</em>}
                                            {c.year && <> ({c.year})</>}
                                            {c.pmid && (
                                                <>
                                                    {" "}
                                                    <a
                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${c.pmid}/`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="font-semibold hover:underline"
                                                        style={{ color: TEAL }}
                                                    >
                                                        PMID: {c.pmid}
                                                    </a>
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            </Section>
                        )}
                    </div>
                )
            case "specs":
                return (
                    <div className="max-w-2xl">
                        <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
                            {specs.map((spec, i) => (
                                <div
                                    key={spec.label}
                                    className={`flex flex-col sm:flex-row sm:items-center py-3.5 px-5 ${
                                        i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                                    }`}
                                >
                                    <span className="sm:w-1/3 text-sm font-bold text-slate-800 mb-1 sm:mb-0">
                                        {spec.label}
                                    </span>
                                    <span className="sm:w-2/3 text-sm text-slate-600 font-mono tracking-tight">
                                        {spec.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            case "storage":
                return (
                    <div className="max-w-2xl space-y-8">
                        {storageConditions && (
                            <Section title="Storage Conditions">
                                <Paragraphs text={storageConditions} splitOn="line" />
                            </Section>
                        )}
                        {handlingNotes && (
                            <Section title="Handling Notes">
                                <Paragraphs text={handlingNotes} splitOn="line" />
                            </Section>
                        )}
                        {stabilityData && (
                            <Section title="Stability">
                                <Paragraphs text={stabilityData} splitOn="line" />
                            </Section>
                        )}
                    </div>
                )
            case "faq":
                return (
                    <div className="max-w-3xl">
                        <FaqAccordion items={faqItems} />
                    </div>
                )
            case "coa":
                return (
                    <div className="max-w-3xl">
                        <COASection product={product} legacyCoaUrl={legacyCoaUrl} />
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Desktop: horizontal tab bar (no longer sticky) */}
            <div className="hidden md:block border-b border-slate-200">
                <nav className="flex flex-wrap" aria-label="Product details">
                    {visibleTabs.map((tab) => {
                        const active = activeTab === tab.id
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={active}
                                className={`relative py-4 px-5 text-[13px] font-bold uppercase tracking-wide whitespace-nowrap transition-colors ${
                                    active ? "text-slate-900" : "text-slate-400 hover:text-slate-700"
                                }`}
                            >
                                {tab.label}
                                {active && (
                                    <span
                                        className="absolute bottom-0 left-0 right-0 h-0.5"
                                        style={{ backgroundColor: TEAL }}
                                    />
                                )}
                            </button>
                        )
                    })}
                </nav>
            </div>

            {/* Desktop: single panel for the active tab */}
            <div className="hidden md:block py-8 lg:py-10">
                {renderContent(activeTab)}
            </div>

            {/* Mobile: vertical collapsible accordion — each section clearly tappable */}
            <div className="md:hidden divide-y divide-slate-200 border-y border-slate-200">
                {visibleTabs.map((tab) => {
                    const isOpen = openMobile === tab.id
                    return (
                        <div key={tab.id}>
                            <button
                                onClick={() => setOpenMobile(isOpen ? null : tab.id)}
                                aria-expanded={isOpen}
                                className="w-full flex items-center justify-between gap-3 py-4 text-left"
                            >
                                <span
                                    className={`text-[13px] font-bold uppercase tracking-wide transition-colors ${
                                        isOpen ? "text-slate-900" : "text-slate-500"
                                    }`}
                                    style={isOpen ? { color: TEAL } : undefined}
                                >
                                    {tab.label}
                                </span>
                                <ChevronDown
                                    size={20}
                                    className={`flex-shrink-0 text-slate-400 transition-transform ${
                                        isOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {isOpen && <div className="pb-6">{renderContent(tab.id)}</div>}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section>
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
        {children}
    </section>
)

const Paragraphs = ({ text, splitOn = "para" }: { text: string; splitOn?: "para" | "line" }) => {
    const parts = (splitOn === "line" ? text.split("\n") : text.split(/\n\n+/))
        .map((p) => p.trim())
        .filter(Boolean)
    return (
        <div className="space-y-3">
            {parts.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed text-[15px]">
                    {p}
                </p>
            ))}
        </div>
    )
}

const FaqAccordion = ({ items }: { items: FaqItem[] }) => {
    const [open, setOpen] = useState<number>(0)
    return (
        <div className="divide-y divide-slate-100 border-t border-slate-100">
            {items.map((item, i) => {
                const isOpen = open === i
                return (
                    <div key={i} className="py-4">
                        <button
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            aria-expanded={isOpen}
                            className="w-full flex items-start justify-between gap-4 text-left group"
                        >
                            <span
                                className={`text-[15px] leading-relaxed ${
                                    isOpen ? "text-slate-900 font-semibold" : "text-slate-700 font-medium"
                                }`}
                            >
                                {item.q}
                            </span>
                            <span
                                className={`text-slate-400 text-xl leading-none mt-0.5 flex-shrink-0 transition-transform ${
                                    isOpen ? "rotate-45" : ""
                                }`}
                            >
                                +
                            </span>
                        </button>
                        {isOpen && (
                            <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{item.a}</p>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default ProductDetailTabs
